import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Pagination,
} from "@mui/material";
import { Edit, Delete, Print, Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useDelete from "../../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AmbulanceDriverPaymentForm from "./AmbulanceDriverPaymentForm";
import CloseIcon from "@mui/icons-material/Close";

const AmbulanceDriverPaymentList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);

  const {
    data: payments,
    refetch,
    isLoading,
    isError,
  } = useFetchObjects(
    ["ambulance-driver-payments", query, dateFilter, page, rowsPerPage],
    `ambulance-driver-payments/?page=${page}&page_size=${rowsPerPage}&search=${query}&date=${dateFilter}`,
    token
  );

  const { handleDelete, DeleteMessageComponent, ConfirmDialog } = useDelete("ambulance-driver-payments", token);

  useEffect(() => {
    refetch();
  }, [query, dateFilter, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (payments && payments.count !== undefined) {
      setTotalPayments(payments.count);
    }
  }, [payments]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEditClick = (payment) => {
    setCurrentPayment(payment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPayment(null);
  };

  const handlePrintClick = (payment) => {
    setPrintData(payment);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
          <html>
            <head>
              <title>Ambulance Driver Payment Receipt</title>
              <style>
                * { box-sizing: border-box; }
                body {
                  margin: 0;
                  padding: 24px;
                  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
                  background: #eef2f7;
                  color: #14213d;
                }
                .receipt-shell {
                  max-width: 820px;
                  margin: 0 auto;
                }
                .receipt-card {
                  background: #fff;
                  border: 1px solid #d7e0ea;
                  border-radius: 18px;
                  overflow: hidden;
                  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
                }
                .receipt-header {
                  padding: 24px 28px 18px;
                  background: linear-gradient(135deg, #0f4c81 0%, #1f6aa5 100%);
                  color: #fff;
                }
                .receipt-kicker {
                  margin: 0 0 8px;
                  font-size: 12px;
                  letter-spacing: 0.12em;
                  text-transform: uppercase;
                  opacity: 0.85;
                }
                .receipt-title-row {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  gap: 16px;
                }
                .receipt-title {
                  margin: 0;
                  font-size: 30px;
                  font-weight: 700;
                  line-height: 1.2;
                }
                .receipt-date {
                  text-align: right;
                  font-size: 14px;
                  line-height: 1.6;
                  white-space: nowrap;
                }
                .receipt-body {
                  padding: 28px;
                }
                .amount-box {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 24px;
                  padding: 18px 20px;
                  background: #f8fbff;
                  border: 1px solid #d8e7f5;
                  border-radius: 14px;
                }
                .amount-label {
                  margin: 0;
                  color: #5b6b7f;
                  font-size: 13px;
                  text-transform: uppercase;
                  letter-spacing: 0.08em;
                }
                .amount-value {
                  margin: 6px 0 0;
                  font-size: 30px;
                  font-weight: 800;
                  color: #0f4c81;
                }
                .status-badge {
                  padding: 8px 14px;
                  border-radius: 999px;
                  background: #e8f3ec;
                  color: #1f6b3b;
                  font-size: 13px;
                  font-weight: 700;
                }
                .details-grid {
                  display: grid;
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                  gap: 14px;
                }
                .detail-item {
                  padding: 14px 16px;
                  border-radius: 12px;
                  background: #fcfdff;
                  border: 1px solid #e5ebf2;
                }
                .detail-label {
                  margin: 0 0 6px;
                  font-size: 12px;
                  color: #6b7a8c;
                  text-transform: uppercase;
                  letter-spacing: 0.08em;
                }
                .detail-value {
                  margin: 0;
                  font-size: 17px;
                  font-weight: 600;
                  color: #14213d;
                  word-break: break-word;
                }
                .receipt-footer {
                  margin-top: 24px;
                  padding-top: 18px;
                  border-top: 1px dashed #ccd6e0;
                  font-size: 13px;
                  color: #6b7a8c;
                  text-align: center;
                }
                @media print {
                  body {
                    padding: 0;
                    background: #fff;
                  }
                  .receipt-card {
                    border: none;
                    border-radius: 0;
                    box-shadow: none;
                  }
                }
                @media (max-width: 640px) {
                  body {
                    padding: 12px;
                  }
                  .receipt-header,
                  .receipt-body {
                    padding: 18px;
                  }
                  .receipt-title-row,
                  .amount-box {
                    display: block;
                  }
                  .receipt-date {
                    text-align: left;
                    margin-top: 10px;
                  }
                  .details-grid {
                    grid-template-columns: 1fr;
                  }
                }
              </style>
            </head>
            <body>${printRef.current.innerHTML}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Grid container spacing={4} mb={4} pb={5}>
      <Grid item xs={12}>
        <Box
          padding="25px"
          backgroundColor="white"
          boxShadow={3}
          borderRadius={2}
        >
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Typography variant="h5" fontWeight={700} color="primary">
              {t("finance.ambulanceDriverPaymentList", { defaultValue: "Ambulance Driver Payments List" })}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/finance/fees-collection/ambulance/driver/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("common.addNew", { defaultValue: "Add New" })}
            </Button>
          </Box>

          {/* Filters */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            mb={3}
            justifyContent="space-between"
          >
            <TextField
              label={t("reception.searchByDriver")}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: <Search color="action" />,
              }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <TextField
              label={t("reception.filterByDate")}
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </Stack>

          {/* Table */}
          {isLoading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2">{t("reception.loading")}</Typography>
            </Box>
          ) : isError ? (
            <Box textAlign="center" color="red" mt={4}>
              <Typography variant="body2">{t("reception.errorFetchingData")}</Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.date", { defaultValue: "Date" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.driverName", { defaultValue: "Driver Name" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.ambulanceCode", { defaultValue: "Ambulance Code" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.paymentType", { defaultValue: "Payment Type" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.amount", { defaultValue: "Amount" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.actions", { defaultValue: "Actions" })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{payments?.results?.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell align="center">
                        <Typography>{new Date(payment.payment_date).toLocaleDateString()}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.driver_display || payment.driver?.name || ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.ambulance_code}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.payment_type}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.amount}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          aria-label="edit"
                          onClick={() => handleEditClick(payment)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="print"
                          onClick={() => handlePrintClick(payment)}
                        >
                          <Print />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="delete"
                          onClick={() => handleDelete(payment.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Total Count */}
        <Box mt={2} textAlign="right">
          <Typography variant="body2">
            {t("reception.totalRecords", { defaultValue: "Total Records" })}:{" "}
            <Typography component="span" fontWeight="bold">
              {totalPayments}
            </Typography>
          </Typography>
        </Box>
      </Grid>
      <DeleteMessageComponent />
      <ConfirmDialog />
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("finance.editAmbulanceDriverPayment", {
            defaultValue: "Edit Ambulance Driver Payment",
          })}
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {currentPayment && (
            <AmbulanceDriverPaymentForm
              payment={currentPayment}
              onSuccess={handleCloseDialog}
            />
          )}
        </DialogContent>
      </Dialog>
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <div className="receipt-shell">
            <div className="receipt-card">
              <div className="receipt-header">
                <p className="receipt-kicker">Finance Department</p>
                <div className="receipt-title-row">
                  <h2 className="receipt-title">Ambulance Driver Payment Receipt</h2>
                  <div className="receipt-date">
                    <div><strong>Date:</strong> {new Date(printData.payment_date).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>

              <div className="receipt-body">
                <div className="amount-box">
                  <div>
                    <p className="amount-label">Total Amount</p>
                    <p className="amount-value">{printData.amount}</p>
                  </div>
                  <div className="status-badge">{printData.payment_type}</div>
                </div>

                <div className="details-grid">
                  <div className="detail-item">
                    <p className="detail-label">Driver</p>
                    <p className="detail-value">{printData.driver_display || printData.driver?.name || ""}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Driver Phone</p>
                    <p className="detail-value">{printData.driver_phone || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Ambulance Code</p>
                    <p className="detail-value">{printData.ambulance_code || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Plate Number</p>
                    <p className="detail-value">{printData.plate_number || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Trip Reference</p>
                    <p className="detail-value">{printData.trip_reference || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Payment Type</p>
                    <p className="detail-value">{printData.payment_type || "N/A"}</p>
                  </div>
                </div>

                <div className="receipt-footer">
                  This receipt confirms the ambulance driver payment record.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Grid>
  );
};

export default AmbulanceDriverPaymentList;
